import { Component, ChangeDetectionStrategy, inject, signal, effect, OnInit, OnDestroy, computed, input } from "@angular/core";
import { FormsModule, ReactiveFormsModule, Validators, FormGroup, FormControl } from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ConfirmationService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { RadioButtonModule } from "primeng/radiobutton";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { ChipModule } from "primeng/chip";
import { FloatLabelModule } from "primeng/floatlabel";
import { FileUploadModule, FileSelectEvent } from "primeng/fileupload";
import { MessageModule } from "primeng/message";
import { TooltipModule } from "primeng/tooltip";

import { Editor, Toolbar } from "ngx-editor";
import { NgxEditorModule } from "ngx-editor";

import { LanguageCode, QuoteType, TermsClient } from "src/api-service";
import { selectIsSuperUser, selectUserContext } from "src/app/store/user/user.selectors";
import { checkForDuplicatesInArray, strictEmailValidator } from "src/app/shared/validators/validations";
import { SendQuoteRequest } from "src/app/shared/interfaces/interfaces";
import { selectIsSubscriptionQuote, selectQuoteState } from "src/app/store/pages/quote/quote.selectors";
import { selectVersion } from "src/app/store/pages/quote/version/version.selectors";
import { QuoteActions } from "src/app/store/pages/quote/quote.actions";
// PreviewActions moved to QuoteActions
import { UserActions } from "src/app/store/user/user.actions";
import { CoreErrorsComponent } from "src/app/shared/components/core-errors/core-errors.component";
import { TG_SEND_FLOOR_SUBSCRIPTION, TG_SEND_WARN_SUBSCRIPTION } from "src/app/shared/constants/tg.constants";

import { TEMPLATES_SV as svQuoteMessage } from "src/assets/templates/quote-to-customer-message-templates";
import { TEMPLATES_EN as enQuoteMessage } from "src/assets/templates/quote-to-customer-message-templates";
import { TEMPLATES_FI as fiQuoteMessage } from "src/assets/templates/quote-to-customer-message-templates";
import { TEMPLATES_NO as noQuoteMessage } from "src/assets/templates/quote-to-customer-message-templates";
import { TEMPLATES_DA as daQuoteMessage } from "src/assets/templates/quote-to-customer-message-templates";

@Component({
    selector: "app-send-quote-dialog",
    templateUrl: "./send-quote-dialog.component.html",
    styleUrls: ["./send-quote-dialog.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        NgxEditorModule,
        CoreErrorsComponent,
        ButtonModule,
        InputTextModule,
        InputGroupModule,
        InputGroupAddonModule,
        RadioButtonModule,
        ToggleSwitchModule,
        ChipModule,
        FloatLabelModule,
        FileUploadModule,
        MessageModule,
        TooltipModule,
    ],
})
export class SendQuoteDialogComponent implements OnInit, OnDestroy {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    public readonly ref = inject(DynamicDialogRef, { optional: true });

    /**
     * True when rendered as a panel inside a flow stepper rather than as a standalone dialog.
     * The stepper then owns navigation and closing, so this component hides its own footer and
     * stops closing the (absent) dialog ref.
     */
    public readonly embedded = input(false);

    private readonly store = inject(Store);
    private readonly termsClient = inject(TermsClient);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly translate = inject(TranslateService);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    private readonly quote = toSignal(this.store.select(selectQuoteState));
    private readonly user = toSignal(this.store.select(selectUserContext));
    public readonly selectedVersion = toSignal(this.store.select(selectVersion));
    private readonly isSubscriptionQuote = toSignal(this.store.select(selectIsSubscriptionQuote));
    public readonly isSuperUser = toSignal(this.store.select(selectIsSuperUser), { initialValue: false });

    // =========================================================================
    // COMPUTED
    // =========================================================================
    /**
     * Maps each country to its primary language.
     * Each country supports its primary language + English.
     * Other languages fall back to English.
     */
    private readonly primaryLanguageMap: Record<string, string> = {
        'SE': 'SV',
        'DK': 'DA',
        'NO': 'NO',
        'FI': 'FI',
    };

    public readonly currentTG = computed(() => this.selectedVersion()?.summary?.grossMargin1 ?? 100);

    public readonly tgFloor = TG_SEND_FLOOR_SUBSCRIPTION;

    public readonly tgWarn = TG_SEND_WARN_SUBSCRIPTION;

    // TG-spärr och TG-varning gäller endast abonnemangsoffert - vanlig offert får skickas oavsett marginal
    public readonly isTGBelowFloor = computed(() =>
        (this.isSubscriptionQuote() ?? false) && this.currentTG() < this.tgFloor && !this.isSuperUser(),
    );
    public readonly isTGBelowWarn = computed(() =>
        (this.isSubscriptionQuote() ?? false) && this.currentTG() < this.tgWarn,
    );

    public readonly currentTGFormatted = computed(() => this.currentTG().toFixed(1));

    // Visar rätt gräns i varningsmeddelandet (FLOOR om under FLOOR, annars WARN)
    public readonly relevantThreshold = computed(() =>
        this.currentTG() < this.tgFloor ? this.tgFloor : this.tgWarn,
    );

    /**
     * NOTE: reads formStatus(), not form.valid. A FormGroup's `valid` is a plain property, so
     * reading it inside a computed() registers no dependency and the result is cached forever —
     * the button then never reacts to typing. Declared below the form, since toSignal runs eagerly.
     */
    public readonly canSend = computed(() => this.formStatus() === "VALID" && !this.isTGBelowFloor());

    public readonly pdfFileName = computed(() => {
        const quote = this.quote();
        const version = this.selectedVersion();
        if (!quote) return "quote.pdf";
        const prefix = version?.languageCode?.toLowerCase() === "sv" ? "Offert" : "Quote";
        return `${prefix}-${quote.displayId}.pdf`;
    });

    public readonly termsFileName = computed(() => {
        const countryCode = this.user()?.selectedCompany?.countryCode ?? 'SE';
        const versionLanguage = this.selectedVersion()?.languageCode ?? 'EN';
        const effectiveLanguage = this.getEffectiveTermsLanguage(countryCode, versionLanguage);

        const quoteTermsLangMap: Record<string, string> = {
            'SV': 'Allmänna villkor',
            'DA': 'Generelle vilkår',
            'NO': 'Generelle vilkår',
            'FI': 'Yleiset ehdot',
            'EN': 'Terms and Conditions',
        };
        const subscriptionTermsLangMap: Record<string, string> = {
            'SV': 'Abonnemangsvillkor',
            'DA': 'Abonnementsvilkår',
            'NO': 'Abonnementsvilkår',
            'FI': 'Tilausehdot',
            'EN': 'Subscription Terms',
        };
        const langMap = this.isSubscriptionQuote() ? subscriptionTermsLangMap : quoteTermsLangMap;
        return (langMap[effectiveLanguage] ?? langMap['EN']) + '.pdf';
    });

    // =========================================================================
    // LOCAL STATE
    // =========================================================================
    public files: File[] = [];
    public validCcEmails: string[] = [];
    public readonly isToggleChecked = signal(true);
    public readonly sendToSelf = signal(false);

    public messageEditor!: Editor;
    public readonly toolbar: Toolbar = [
        ["bold", "italic"],
        ["underline", "strike"],
        ["ordered_list", "bullet_list"],
        [{ heading: ["h1", "h2", "h3", "h4", "h5", "h6"] }],
        ["text_color", "background_color"],
        ["align_left", "align_center", "align_right", "align_justify"],
        ["link", "image"],
    ];

    // =========================================================================
    // FORM STATE
    // =========================================================================
    public readonly form = new FormGroup({
        senderEmail: new FormControl({ value: "", disabled: true }, [Validators.required]),
        recipientEmail: new FormControl("", [strictEmailValidator(), Validators.required]),
        subject: new FormControl("", [Validators.required]),
        ccEmails: new FormControl("", [strictEmailValidator()]),
        attachTerms: new FormControl(true),
        message: new FormControl("", [Validators.required]),
        company: new FormControl<string | null>(null, [Validators.required]),
    });

    /** Reactive mirror of the form's validity — see canSend(). */
    private readonly formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });


    private formPatched = false;

    // =========================================================================
    // EFFECTS
    // =========================================================================
    private readonly patchFormEffect = effect(() => {
        const quote = this.quote();
        const user = this.user();
        const version = this.selectedVersion();

        if (quote && user && version && !this.formPatched) {
            this.formPatched = true;
            const defaultSendToSelf = user.defaultSendQuoteToSelf ?? false;

            this.sendToSelf.set(defaultSendToSelf);

            this.form.patchValue({
                senderEmail: user.email,
                recipientEmail: defaultSendToSelf ? user.email : quote.salesInfo?.contactEmail ?? "",
                subject: this.getQuoteSubjectTemplateByLanguage(version.languageCode) + quote.displayId,
                company: user.selectedCompany?.id,
                message: this.getQuoteEmailTemplateByLanguage(version.languageCode, quote, user),
            });

            if (defaultSendToSelf) {
                this.form.get("recipientEmail")?.disable();
            } else {
                this.form.get("recipientEmail")?.enable();
            }
        }
    });

    // =========================================================================
    // LIFECYCLE
    // =========================================================================
    public ngOnInit(): void {
        this.messageEditor = new Editor({
            attributes: {
                style: "padding:20px;",
            },
        });
    }

    public ngOnDestroy(): void {
        this.messageEditor?.destroy();
    }

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public onSendToSelfChange(value: boolean): void {
        this.sendToSelf.set(value);
        this.store.dispatch(UserActions.updateDefaultSendQuoteToSelf({ defaultSendQuoteToSelf: value }));

        const user = this.user();
        const quote = this.quote();
        const recipientEmailControl = this.form.get("recipientEmail");

        if (value) {
            recipientEmailControl?.setValue(user?.email ?? "");
            recipientEmailControl?.disable();
        } else {
            recipientEmailControl?.setValue(quote?.salesInfo?.contactEmail ?? "");
            recipientEmailControl?.enable();
        }
    }

    public onSubmit(): void {
        if (this.isTGBelowFloor()) return;

        if (this.isTGBelowWarn()) {
            this.confirmationService.confirm({
                message: this.translate.instant("SEND_QUOTE.TG_BELOW_WARN_CONFIRM.TEXT", { currentTG: this.currentTGFormatted(), threshold: this.relevantThreshold() }),
                header: this.translate.instant("SEND_QUOTE.TG_BELOW_WARN_CONFIRM.TITLE"),
                icon: "pi pi-exclamation-triangle",
                acceptLabel: this.translate.instant("SEND_QUOTE.TG_BELOW_WARN_CONFIRM.ACCEPT"),
                rejectLabel: this.translate.instant("ABORT"),
                accept: () => { this.doSend(); },
            });
            return;
        }

        this.doSend();
    }

    private doSend(): void {
        const quote = this.quote();
        const user = this.user();
        const version = this.selectedVersion();

        if (!quote || !user || !version) return;

        const request: SendQuoteRequest = {
            quoteId: quote.id,
            versionId: version.id,
            recipientEmail: this.form.get("recipientEmail")?.value ?? "",
            ccEmails: this.validCcEmails ?? [],
            subject: this.form.value.subject ?? "",
            message: this.form.value.message ?? "",
            senderEmail: user.email,
            senderName: user.email,
            language: this.languageHelpMethod(version.languageCode),
            quoteType: this.isSubscriptionQuote() ? QuoteType.Subscription : QuoteType.Regular,
            attachTerms: this.form.value.attachTerms ?? true,
            attachmentFiles: this.files,
        };

        this.store.dispatch(QuoteActions.send({ request, dialogKey: 'SENDING_QUOTE', sendToSelf: this.sendToSelf() }));
        this.ref?.close();
    }

    public openPdfPreview(): void {
        const quote = this.quote();
        const version = this.selectedVersion();

        if (!quote?.id || !version) return;

        this.store.dispatch(QuoteActions.downloadPdf({
            request: {
                quoteId: quote.id,
                versionId: version.id,
                currency: version.currencyCode,
                exchangeRate: version.currencyExchangeRateSnapshot ?? 1,
                language: version.languageCode,
                type: this.isSubscriptionQuote() ? "subscription" : "quote",
            },
            dialogKey: "PREVIEW_QUOTE_PDF",
        }));
    }

    public openTermsPreview(): void {
        const countryCode = this.user()?.selectedCompany?.countryCode ?? 'SE';
        const versionLanguage = this.selectedVersion()?.languageCode ?? 'EN';
        const effectiveLanguage = this.getEffectiveTermsLanguage(countryCode, versionLanguage);
        const isSubscription = this.isSubscriptionQuote() ?? false;
        this.termsClient.get(countryCode, effectiveLanguage, isSubscription).subscribe({
            next: (file) => {
                const blobUrl = URL.createObjectURL(file.data);
                window.open(blobUrl, "_blank", "noopener");
            },
            error: (err) => {
                console.error("Failed to open terms:", err);
            },
        });
    }

    public languageHelpMethod(language: string): LanguageCode {
        switch (language?.toLowerCase()) {
            case "sv": return LanguageCode.SV;
            case "en": return LanguageCode.EN;
            case "fi": return LanguageCode.FI;
            case "no": return LanguageCode.NO;
            case "da": return LanguageCode.DA;
            default: return LanguageCode.EN;
        }
    }

    public addCcRecipient(): void {
        const ccEmailsControl = this.form.get("ccEmails");
        const email = ccEmailsControl?.value?.trim() ?? "";

        if (!email) return;

        ccEmailsControl?.markAsTouched();

        if (ccEmailsControl?.invalid) return;

        if (this.validCcEmails.includes(email)) {
            ccEmailsControl?.setValue("");
            return;
        }

        this.validCcEmails.push(email);
        ccEmailsControl?.setValidators([strictEmailValidator(), checkForDuplicatesInArray(this.validCcEmails)]);
        ccEmailsControl?.setValue("");
        ccEmailsControl?.updateValueAndValidity();
    }

    public removeCcRecipient(email: string): void {
        const index = this.validCcEmails.indexOf(email);
        if (index >= 0) {
            this.validCcEmails.splice(index, 1);
        }
    }

    public onAttachTermsChange(checked: boolean): void {
        if (!checked) {
            this.confirmationService.confirm({
                message: this.translate.instant("SEND_QUOTE.REMOVE_TERMS_DIALOG.TEXT"),
                header: this.translate.instant("SEND_QUOTE.REMOVE_TERMS_DIALOG.TITLE"),
                icon: "pi pi-exclamation-triangle",
                acceptLabel: this.translate.instant("OK"),
                rejectLabel: this.translate.instant("ABORT"),
                accept: () => {
                    this.isToggleChecked.set(false);
                    this.form.patchValue({ attachTerms: false });
                },
                reject: () => {
                    this.isToggleChecked.set(true);
                    this.form.patchValue({ attachTerms: true });
                },
            });
        } else {
            this.isToggleChecked.set(true);
            this.form.patchValue({ attachTerms: true });
        }
    }

    public removeTermsChip(): void {
        this.onAttachTermsChange(false);
    }

    public onFilesSelected(event: FileSelectEvent): void {
        this.files = [...this.files, ...event.files];
    }

    public removeFile(file: File): void {
        const index = this.files.indexOf(file);
        if (index >= 0) {
            this.files.splice(index, 1);
        }
    }

    public closeDialog(): void {
        this.ref?.close();
    }

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================

    /**
     * Determines the effective terms language based on country and requested language.
     * Each country supports its primary language + English. Other languages fall back to English.
     */
    private getEffectiveTermsLanguage(countryCode: string, requestedLanguage: string): string {
        const requested = requestedLanguage.toUpperCase();

        // English is always valid
        if (requested === 'EN') {
            return 'EN';
        }

        // Primary language for the country is valid
        const primary = this.primaryLanguageMap[countryCode.toUpperCase()];
        if (primary && requested === primary) {
            return requested;
        }

        // Fallback to English
        return 'EN';
    }

    private getQuoteSubjectTemplateByLanguage(lang: string): string {
        switch (lang?.toLowerCase()) {
            case "sv": return svQuoteMessage["SUBJECT"];
            case "en": return enQuoteMessage["SUBJECT"];
            case "fi": return fiQuoteMessage["SUBJECT"];
            case "no": return noQuoteMessage["SUBJECT"];
            case "da": return daQuoteMessage["SUBJECT"];
            default: return enQuoteMessage["SUBJECT"];
        }
    }

    private getQuoteEmailTemplateByLanguage(lang: string, quote: any, user: any): string {
        const firstNameOfContact = quote.salesInfo?.contactName?.split(" ")[0] ?? "";
        const checkedPhoneNumber = user.phoneNumber ? `<br>${user.phoneNumber}` : " ";
        const replacements: Record<string, string> = {
            NAME: user.name,
            QUOTEID: quote.displayId?.toString() ?? "",
            MAIL: user.email,
            RECEIPIENT: firstNameOfContact,
            PHONE: checkedPhoneNumber,
        };

        let emailTemplate: string;
        switch (lang?.toLowerCase()) {
            case "sv": emailTemplate = svQuoteMessage["BODY"]; break;
            case "en": emailTemplate = enQuoteMessage["BODY"]; break;
            case "fi": emailTemplate = fiQuoteMessage["BODY"]; break;
            case "no": emailTemplate = noQuoteMessage["BODY"]; break;
            case "da": emailTemplate = daQuoteMessage["BODY"]; break;
            default: emailTemplate = enQuoteMessage["BODY"];
        }

        return emailTemplate.replace(/\b(RECEIPIENT|QUOTEID|MAIL|NAME|PHONE)\b/g, match => replacements[match] || match);
    }
}
